// import { makeObservable, observable, computed } from 'mobx'
import get from 'lodash/get'
import { isEmpty } from 'lodash'
import axios from 'axios'
import { EV_AUTHORIZE_ERROR, EV_CONTENT_UPDATE_LANGUAGE } from '../constant'
import BaseStore from '../base/BaseStore'

class ContainerStore extends BaseStore {
  constructor() {
    super()
    this.BASE_STORE_DEBUG_COLOR = 'Cyan'
    this.accessToken = ''
    this.baseUrl = ''
    this.language = ''
    this._init()
    this._initCancelToken()
  }

  _init() {
    this._errorMiddlewares = [
      this._authorizeErrorHandler,
      this._normalErrorHandler,
    ]
    this._networkStatus = 'ONLINE'
  }

  _initCancelToken() {
    const CancelToken = axios.CancelToken
    this._source = CancelToken.source()
  }

  _isRequestSuccess(response) {
    if (!response) {
      return false
    }
    const { status, body = {} } = response
    return (
      status === 200 || // GET
      status === 304 || // GET - CACHE
      status === 201 || // POST
      status === 204 || // DELETE
      body.status === 'success'
    )
  }

  // -----------------------------------------------------//
  //  Error Middleware
  // -----------------------------------------------------//

  _normalErrorHandler(response) {
    const status = get(response, 'code')
    if (status === 401) return
    const message =
      get(response, 'message') ||
      get(response, 'data.message') ||
      get(response, 'data.error.message') ||
      'unknown'

    const ins = new Error(message)
    ins.errorId = message
    ins.extra = get(response, 'data.data.extra')
    ins.status = status
    ins.messageKey = message
    throw ins
  }

  _authorizeErrorHandler(response, context = {}) {
    if (!context.autoLogout) return
    const status = get(response, 'code')
    // error txtUnauthorized
    if (status === 401) {
      this.emitAuthorizeError()

      const ins = new Error('txtUnauthorized')
      ins.errorId = 'txtUnauthorized'
      ins.extra = get(response, 'data.data.extra')
      ins.status = status
      ins.messageKey = 'txtUnauthorized'
      throw ins
    }
  }

  _getFullUrl(endpoint, baseUrl) {
    const basePath = isEmpty(this.baseUrl) ? baseUrl : this.baseUrl
    return `${basePath}${endpoint}`
  }

  async httpRequest(
    {
      baseUrl,
      endpoint,
      method,
      body = {},
      params = {},
      headers = {},
      ...extra
    },
    options = { autoLogout: true }
  ) {
    const { isOnline } = this
    const errorContext = {
      autoLogout: options.autoLogout,
    }

    if (!isOnline) throw new Error('No internet connection')

    const url = this._getFullUrl(endpoint, baseUrl)
    try {
      headers.Authorization = this.accessToken
      const response = await axios({
        method,
        url,
        headers,
        withCredentials: true,
        params,
        data: body,
        cancelToken: this._source.token,
        ...extra,
      })
      if (!this._isRequestSuccess(response))
        this._onErrorMiddleware(response, errorContext)

      return response
    } catch (err) {
      this._onErrorMiddleware(
        err.response || err.message.response,
        errorContext
      )
    }
  }

  _onErrorMiddleware(response, context) {
    this._errorMiddlewares.some((func) => func(response, context))
  }

  // ---------------------------------------------//
  //  Public
  // ---------------------------------------------//

  get isOnline() {
    return this._networkStatus === 'ONLINE'
  }

  emitEvent(eventName, data) {
    this.emit(eventName, data)
  }

  onEvent(eventName, _callbackFunction) {
    this.on(eventName, (data) => {
      _callbackFunction(data)
    })
  }

  emitUpdateLanguage(lang) {
    this.emitEvent(EV_CONTENT_UPDATE_LANGUAGE, lang)
  }

  emitAuthorizeError() {
    this.emitEvent(EV_AUTHORIZE_ERROR, '')
  }

  onAuthorizeError(_callbackFunction) {
    this.onEvent(EV_AUTHORIZE_ERROR, _callbackFunction)
  }

  onUpdateLanguage(_callbackFunction) {
    this.onEvent(EV_CONTENT_UPDATE_LANGUAGE, _callbackFunction)
  }
}

export default new ContainerStore()
